/* ============================================================
   geometry.js  —  Pure geometry math for parking slot generation
   ============================================================ */

import * as turf from '@turf/turf';

function deg2rad(d) { return d * Math.PI / 180; }

export function pointTo(latlng, distMeters, bearingDeg) {
  const pt = turf.destination(
    turf.point([latlng[0], latlng[1]]),
    distMeters,
    bearingDeg,
    { units: 'meters' }
  );
  return pt.geometry.coordinates;
}

export function bearingOf(start, end) {
  return turf.bearing(turf.point(start), turf.point(end));
}

export function distanceOf(start, end) {
  return turf.distance(turf.point(start), turf.point(end), { units: 'meters' });
}

export function generateSlotsForRow(row, opts) {
  opts = opts || {};
  const w  = opts.slotWidth  != null ? opts.slotWidth  : 2.5;
  const L  = opts.slotLength != null ? opts.slotLength : 5.0;
  const a  = opts.angle      != null ? opts.angle      : 90;
  const side = opts.side || 'right';
  const slant = opts.slantSign === -1 ? -1 : 1;
  const facing = opts.facing || 'in';
  const accessibleEvery = opts.accessibleEvery || 0;
  const accessibleWidth = opts.accessibleWidth != null ? opts.accessibleWidth : 3.5;
  const rowLabel = opts.rowLabel || row.label || 'A';
  const obstacles = opts.obstacles || [];

  const start = row.start;
  const end = row.end;
  if (!start || !end) return [];

  const lengthM  = distanceOf(start, end);
  const bearing  = bearingOf(start, end);
  const θ = deg2rad(a);

  const stepM = a <= 0.5 ? L : (w / Math.sin(θ));
  const accStep = a <= 0.5 ? L : (accessibleWidth / Math.sin(θ));
  if (stepM <= 0.05) return [];

  const sideSigns = side === 'both' ? [+1, -1] : (side === 'right' ? [+1] : [-1]);

  const slots = [];
  for (const sg of sideSigns) {
    const perpBearing = bearing + 90 * sg;

    let cursorAlong = 0;
    let i = 0;
    while (true) {
      const isAcc = accessibleEvery > 0 && i > 0 && (i % accessibleEvery === 0);
      const useW = isAcc ? accessibleWidth : w;
      const useStep = isAcc ? accStep : stepM;

      if (cursorAlong + useStep > lengthM + 0.01) break;

      const anchor = pointTo(start, cursorAlong, bearing);

      let cornersLocal;
      if (a <= 0.5) {
        const len = a === 0 ? L : (useW / Math.sin(θ));
        cornersLocal = [
          [0, 0],
          [len, 0],
          [len, useW],
          [0, useW]
        ];
      } else {
        const cos = Math.cos(θ);
        const sin = Math.sin(θ);
        cornersLocal = [
          [0, 0],
          [useStep, 0],
          [useStep + slant * L * cos, L * sin],
          [          slant * L * cos, L * sin]
        ];
      }

      const cornersLL = cornersLocal.map(([dr, dp]) => {
        let p = anchor;
        if (Math.abs(dr) > 0.001) p = pointTo(p, Math.abs(dr), dr >= 0 ? bearing : bearing + 180);
        if (Math.abs(dp) > 0.001) p = pointTo(p, Math.abs(dp), dp >= 0 ? perpBearing : perpBearing + 180);
        return p;
      });
      cornersLL.push(cornersLL[0]);

      let label;
      if (side === 'both') {
        label = `${rowLabel}${i+1}${sg === 1 ? '' : "'"}`;
      } else {
        label = `${rowLabel}${i+1}`;
      }

      const slot = {
        id: `${row.id}-${sg === 1 ? 'R' : 'L'}-${i}`,
        rowId: row.id,
        polygon: cornersLL,
        label,
        side: sg === 1 ? 'right' : 'left',
        index: i,
        accessible: isAcc,
        blocked: false,
        angleDeg: a,
        facing,
      };

      if (obstacles.length) {
        const slotFt = turf.polygon([cornersLL]);
        for (const obs of obstacles) {
          try {
            if (turf.booleanIntersects(slotFt, obs)) {
              slot.blocked = true;
              break;
            }
          } catch (e) {
            // ignore degenerate polygons
          }
        }
      }

      slots.push(slot);
      cursorAlong += useStep;
      i++;
    }
  }

  return slots;
}

export function recommendedLaneWidth(angleDeg) {
  if (angleDeg >= 80) return 6.0;
  if (angleDeg >= 70) return 5.5;
  if (angleDeg >= 55) return 4.8;
  if (angleDeg >= 40) return 4.0;
  if (angleDeg >= 25) return 3.5;
  return 3.5;
}

export function step(angle, w, L) {
  if (angle <= 0.5) return L;
  return w / Math.sin(angle * Math.PI / 180);
}

export function autoFillPolygon(boundary, opts, obstacles) {
  opts = opts || {};
  obstacles = obstacles || [];
  const w  = opts.slotWidth  || 2.5;
  const L  = opts.slotLength || 5.0;
  const a  = opts.angle      || 90;
  const slant = opts.slantSign === -1 ? -1 : 1;
  const laneWidth = opts.laneWidth || recommendedLaneWidth(a);
  const doubleSided = opts.doubleSided !== false;
  const maxRows = opts.maxRows || 24;
  const accessibleEvery = opts.accessibleEvery || 0;

  if (!boundary || !boundary.polygon || boundary.polygon.length < 3) return { rows: [], lanes: [] };

  const poly = boundary.polygon;

  // Row orientation: either user-supplied (in degrees, 0 = north, clockwise)
  // or derived from the polygon's longest edge. Override exists because the
  // longest-edge heuristic fails on trapezoidal lots where the natural lane
  // direction isn't the longest side.
  let bearing;
  if (opts.bearing != null && Number.isFinite(opts.bearing)) {
    bearing = opts.bearing;
  } else {
    let longest = { i: 0, len: 0 };
    for (let i = 0; i < poly.length - 1; i++) {
      const d = distanceOf(poly[i], poly[i + 1]);
      if (d > longest.len) longest = { i, len: d };
    }
    bearing = bearingOf(poly[longest.i], poly[longest.i + 1]);
  }

  const turfPoly = turf.polygon([poly]);
  const centroid = turf.centroid(turfPoly).geometry.coordinates;

  const θ = a * Math.PI / 180;
  const rowDepth = a <= 0.5 ? w : (L * Math.sin(θ));
  const moduleDepth = laneWidth + (doubleSided ? 2 * rowDepth : rowDepth);

  const perpBearing = bearing + 90;
  const rows = [];
  const lanes = [];

  const bbox = turf.bbox(turfPoly);
  const diagMeters = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[2], bbox[3]]), { units: 'meters' });
  const span = diagMeters * 0.9;

  // Find the polygon's actual extent along the perpendicular axis by
  // projecting each vertex's signed distance from the centroid onto perpBearing.
  // This replaces the centroid-symmetric grid that left trapezoids with a
  // dropped first/last module.
  //
  // Signed perpendicular offset of vertex v relative to centroid:
  //   d_perp = distance(centroid, v) * cos(bearingOf(centroid, v) - perpBearing)
  // Positive = right of bearing, negative = left.
  let perpMin = +Infinity;
  let perpMax = -Infinity;
  for (let i = 0; i < poly.length - 1; i++) {
    const v = poly[i];
    const d = distanceOf(centroid, v);
    if (d < 1e-6) continue;
    const angleToV = bearingOf(centroid, v);
    const deltaDeg = angleToV - perpBearing;
    const dPerp = d * Math.cos(deltaDeg * Math.PI / 180);
    if (dPerp < perpMin) perpMin = dPerp;
    if (dPerp > perpMax) perpMax = dPerp;
  }
  if (!Number.isFinite(perpMin) || !Number.isFinite(perpMax)) {
    return { rows: [], lanes: [] };
  }

  // Build module-center offsets sweeping across the actual extent. Half-module
  // inset on each side so the rows themselves sit inside the polygon (not on
  // the edge). Capped by maxRows.
  const halfModule = moduleDepth / 2;
  const startOffset = perpMin + halfModule;
  const endOffset = perpMax - halfModule;
  const direction = opts.direction === 'rtl' ? 'rtl' : 'ltr';

  const moduleOrder = [];
  if (endOffset >= startOffset) {
    for (let off = startOffset; off <= endOffset + 1e-6; off += moduleDepth) {
      moduleOrder.push(off);
      if (moduleOrder.length >= maxRows) break;
    }
  }
  if (direction === 'rtl') moduleOrder.reverse();

  for (const offsetCenter of moduleOrder) {
    const moduleCenter = pointTo(centroid, Math.abs(offsetCenter), offsetCenter >= 0 ? perpBearing : perpBearing + 180);

    const probeDist = span;
    const lineA = pointTo(moduleCenter, probeDist, bearing + 180);
    const lineB = pointTo(moduleCenter, probeDist, bearing);
    const probeLine = turf.lineString([lineA, lineB]);

    let clipped;
    try {
      const split = turf.lineSplit(probeLine, turfPoly);
      const inside = split.features.filter(f => {
        const mid = turf.midpoint(turf.point(f.geometry.coordinates[0]), turf.point(f.geometry.coordinates[1]));
        return turf.booleanPointInPolygon(mid, turfPoly);
      });
      if (inside.length === 0) continue;
      let best = inside[0];
      let bestLen = turf.length(best, { units: 'meters' });
      for (const f of inside) {
        const l = turf.length(f, { units: 'meters' });
        if (l > bestLen) { best = f; bestLen = l; }
      }
      clipped = best;
    } catch (e) {
      continue;
    }
    if (!clipped) continue;
    const coords = clipped.geometry.coordinates;
    if (coords.length < 2) continue;
    const segStart = coords[0];
    const segEnd   = coords[coords.length - 1];
    const segLen   = distanceOf(segStart, segEnd);
    if (segLen < step(a, w, L) * 2) continue;

    const rowLabel = String.fromCharCode(65 + rows.length);
    const rowId = `auto-row-${rows.length}`;

    if (doubleSided) {
      // Place each row's centerline at the INNER edge of its slot strip
      // (i.e. at the lane edge). generateSlotsForRow extends slots from the
      // centerline by rowDepth in the perpBearing+90*sg direction. We want
      // each row's slots to extend AWAY from the lane (outward).
      //
      // Upper row centerline at +laneWidth/2 along perpBearing. Its slots
      // must extend further in perpBearing → side:'right' (sg=+1) so
      // perpBearing(slot) = bearing + 90 = perpBearing.
      //
      // Lower row centerline at +laneWidth/2 along perpBearing+180. Its slots
      // must extend further in perpBearing+180 → side:'left' (sg=-1) so
      // perpBearing(slot) = bearing - 90 = perpBearing+180 (mod 360).
      const upperStart = pointTo(segStart, laneWidth / 2, perpBearing);
      const upperEnd   = pointTo(segEnd,   laneWidth / 2, perpBearing);
      const lowerStart = pointTo(segStart, laneWidth / 2, perpBearing + 180);
      const lowerEnd   = pointTo(segEnd,   laneWidth / 2, perpBearing + 180);

      rows.push({
        id: rowId + '-N',
        start: upperStart,
        end: upperEnd,
        label: rowLabel,
        opts: { slotWidth: w, slotLength: L, angle: a, slantSign: slant, side: 'right', facing: 'in', accessibleEvery }
      });
      rows.push({
        id: rowId + '-S',
        start: lowerStart,
        end: lowerEnd,
        label: rowLabel + "'",
        // Slant flipped so the parallelograms mirror across the lane (V-shape).
        opts: { slotWidth: w, slotLength: L, angle: a, slantSign: -slant, side: 'left', facing: 'in', accessibleEvery }
      });
      lanes.push({
        id: `auto-lane-${rows.length}`,
        start: segStart,
        end: segEnd,
        width: laneWidth
      });
    } else {
      rows.push({
        id: rowId,
        start: segStart,
        end: segEnd,
        label: rowLabel,
        opts: { slotWidth: w, slotLength: L, angle: a, slantSign: slant, side: 'right', facing: 'in', accessibleEvery }
      });
    }
  }

  return { rows, lanes };
}

/**
 * Sample geocoordinate waypoints every `stepM` metres along a line from
 * `start` to `end`. Always includes the start (distM=0) and the end (distM=totalM).
 * Each waypoint: { distM, lat, lng }.
 */
export function sampleWaypoints(start, end, stepM = 10) {
  if (!start || !end) return [];
  const total = distanceOf(start, end);
  if (total < 1e-3) return [{ distM: 0, lat: start[1], lng: start[0] }];
  const bearing = bearingOf(start, end);
  const out = [];
  for (let d = 0; d < total; d += stepM) {
    const p = d === 0 ? start : pointTo(start, d, bearing);
    out.push({ distM: +d.toFixed(2), lat: p[1], lng: p[0] });
  }
  // Always pin the final endpoint exactly so the last waypoint matches `end`.
  out.push({ distM: +total.toFixed(2), lat: end[1], lng: end[0] });
  return out;
}

export function buildLanePolygon(lane) {
  if (!lane.start || !lane.end || !lane.width) return null;
  const bearing = bearingOf(lane.start, lane.end);
  const half = lane.width / 2;
  const p1 = pointTo(lane.start, half, bearing + 90);
  const p2 = pointTo(lane.end,   half, bearing + 90);
  const p3 = pointTo(lane.end,   half, bearing - 90);
  const p4 = pointTo(lane.start, half, bearing - 90);
  return [p1, p2, p3, p4, p1];
}

export const PG = {
  generateSlotsForRow,
  autoFillPolygon,
  buildLanePolygon,
  sampleWaypoints,
  recommendedLaneWidth,
  distanceOf,
  bearingOf,
  pointTo,
  step,
};
